import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect } from 'react';

import ProductDetailsPage from '@/components/ProductDetailsPage/ProductDetailsPage';
import SeoHead from '@/components/SeoHead/SeoHead';
import { trackViewItem } from '@/helpers/dataLayer';
import {
  buildPageProps,
  fetchDametisProduct,
  fetchProductGroupPageBreadcrumbs,
  fetchStrapiProduct,
  getNotFoundResponse,
  handleRetryDraftProduct,
} from '@/helpers/productDetailsPage';
import {
  generateBreadcrumbStructuredData,
  generateLogoStructuredData,
} from '@/helpers/strapi/structuredData';
import { useSourceContext } from '@/hooks/useSourceContext';
import DefaultLayout from '@/layouts/default';

/**
 * It's heavily encouraged to benefit from optional chaining in this file,
 * given that data from Strapi might be provided corrupted or deficient.
 * In such scenarios, optional chaining allows us to have non-broken components.
 */
const DynamicPage = ({ page, productDetailsData }) => {
  const { asPath } = useRouter();
  const { breadcrumbs } = page;
  const [pathname] = asPath.split('?');
  const { setLeadReference } = useSourceContext();

  useEffect(() => {
    if (pathname) {
      setLeadReference(pathname);
    }
  }, [pathname, setLeadReference]);

  useEffect(() => {
    const {
      salesforce_id: salesforceId,
      name,
      product_group_name: categoryName,
    } = productDetailsData;

    trackViewItem(salesforceId, name, categoryName);
  }, [productDetailsData]);

  return (
    <>
      <SeoHead
        title={page.attributes.title}
        description={page.attributes.description}>
        {generateBreadcrumbStructuredData(breadcrumbs)}
        {generateLogoStructuredData(pathname)}
      </SeoHead>
      <ProductDetailsPage data={productDetailsData} />
    </>
  );
};

DynamicPage.getLayout = function getLayout(page) {
  const breadcrumbs = page?.props?.page?.breadcrumbs;
  return <DefaultLayout breadcrumbs={breadcrumbs}>{page}</DefaultLayout>;
};

DynamicPage.propTypes = {
  page: PropTypes.object,
};

DynamicPage.defaultProps = {
  page: undefined,
};

export async function getServerSideProps({ params }) {
  const { slug } = params;

  if (!slug) {
    return getNotFoundResponse();
  }

  try {
    const { strapiData, dametisId } = await fetchStrapiProduct(slug);

    if (!strapiData) {
      const redirectTo = await handleRetryDraftProduct(slug);
      if (redirectTo) {
        return {
          redirect: {
            destination: redirectTo,
            permanent: false,
          },
        };
      }
      return getNotFoundResponse();
    }

    if (!dametisId) return getNotFoundResponse();

    const [productData, breadcrumbsData] = await Promise.all([
      fetchDametisProduct(dametisId),
      fetchProductGroupPageBreadcrumbs(strapiData, slug),
    ]);

    if (!productData) return getNotFoundResponse();

    const props = buildPageProps(productData, breadcrumbsData, dametisId, slug);

    return {
      props,
    };
  } catch (error) {
    Sentry.captureException(error, {
      extra: { slug },
    });
    return getNotFoundResponse();
  }
}

export default DynamicPage;
